import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetMe } from '../../api/generated/identity/identity';
import type { Me } from '../../api/generated/model';
import { Alert } from '../../design/primitives/Alert';

/**
 * As operacoes de cadastro sao exclusivas de Super Admin no contrato, e quem
 * decide isso e o servidor. A tela nao repete a autorizacao: ela apenas evita
 * desenhar um formulario cujo envio ja se sabe recusado, o que seria pedir dado
 * pessoal para jogar fora.
 */
export function AdminOnly({ children }: { readonly children: (me: Me) => ReactNode }) {
  const { t } = useTranslation();
  const meQuery = useGetMe();
  const me = meQuery.data;

  if (!me) return null;
  if (me.role !== 'super_admin') return <Alert tone="warning">{t('enrollment.adminOnly')}</Alert>;

  return <>{children(me)}</>;
}

export interface Pager {
  readonly cursor: string | undefined;
  readonly canGoBack: boolean;
  readonly goForward: (cursor: string) => void;
  readonly goBack: () => void;
  /**
   * Volta a primeira pagina. Obrigatorio sempre que o FILTRO da listagem muda:
   * o cursor foi emitido para o conjunto anterior e nao significa nada no novo.
   */
  readonly reset: () => void;
}

/**
 * Paginacao por cursor, como o contrato a define: cada pagina devolve
 * `nextCursor` e nada devolve cursor de volta. A pilha guarda os cursores ja
 * visitados para que voltar seja possivel sem inventar um cursor reverso que a
 * API nao tem.
 */
export function usePager(): Pager {
  const [stack, setStack] = useState<readonly string[]>([]);

  return {
    cursor: stack[stack.length - 1],
    canGoBack: stack.length > 0,
    goForward: (cursor) => setStack((current) => [...current, cursor]),
    goBack: () => setStack((current) => current.slice(0, -1)),
    reset: () => setStack([]),
  };
}
